import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, withLatestFrom } from 'rxjs';

import { PaginationResponse } from '../../../shared/models/pagination-response.model';
import * as LinkingActions from './linking.actions';
import * as LinkingSelectors from './linking.selectors';
import { LinkingService } from './linking.service';
import { LinkingDrug } from './models/linking-drug.model';
import { hasActiveLinkingFilters, LinkingFilter } from './models/linking-filter.model';
import { LinkingProduct } from './models/linking-product.model';
import { LinkingState } from './linking.state';

export const loadSalesDrugs$: ReturnType<typeof createEffect> = createEffect(
  (
    actions$ = inject(Actions),
    linkingService = inject(LinkingService),
    store = inject(Store<LinkingState>)
  ) => {
    return actions$.pipe(
      ofType(
        LinkingActions.searchCandidates,
        LinkingActions.refreshCandidates,
        LinkingActions.setSalesPage,
        LinkingActions.setSalesPageSize,
        LinkingActions.setSalesCountryCodes,
        LinkingActions.setSalesSorting
      ),
      withLatestFrom(
        store.select(LinkingSelectors.selectFilters),
        store.select(LinkingSelectors.selectSalesCurrentPage),
        store.select(LinkingSelectors.selectSalesPageSize),
        store.select(LinkingSelectors.selectSalesCountryCodes),
        store.select(LinkingSelectors.selectSalesSortBy),
        store.select(LinkingSelectors.selectSalesIsSortAsc)
      ),
      filter(([, filters]) => hasActiveLinkingFilters(filters)),
      switchMap(
        ([, filters, currentPage, pageSize, countryCodes, sortBy, isSortAsc]: [
          unknown,
          LinkingFilter,
          number,
          number,
          string[],
          string,
          boolean,
        ]) =>
          linkingService
            .getDrugs(filters, currentPage, pageSize, countryCodes, sortBy, isSortAsc)
            .pipe(
              map((response: PaginationResponse<LinkingDrug[]>) =>
                LinkingActions.loadSalesDrugsSuccess({ response })
              ),
              catchError(error =>
                of(
                  LinkingActions.loadSalesDrugsFailure({
                    error: error?.message ?? 'Unknown error loading sales drugs',
                  })
                )
              )
            )
      )
    );
  },
  { functional: true }
);

export const loadPurchaseProducts$: ReturnType<typeof createEffect> = createEffect(
  (
    actions$ = inject(Actions),
    linkingService = inject(LinkingService),
    store = inject(Store<LinkingState>)
  ) => {
    return actions$.pipe(
      ofType(
        LinkingActions.searchCandidates,
        LinkingActions.refreshCandidates,
        LinkingActions.setPurchasePage,
        LinkingActions.setPurchasePageSize,
        LinkingActions.setPurchaseCountryCodes,
        LinkingActions.setPurchaseSorting
      ),
      withLatestFrom(
        store.select(LinkingSelectors.selectFilters),
        store.select(LinkingSelectors.selectPurchaseCurrentPage),
        store.select(LinkingSelectors.selectPurchasePageSize),
        store.select(LinkingSelectors.selectPurchaseCountryCodes),
        store.select(LinkingSelectors.selectPurchaseSortBy),
        store.select(LinkingSelectors.selectPurchaseIsSortAsc)
      ),
      filter(([, filters]) => hasActiveLinkingFilters(filters)),
      switchMap(
        ([, filters, currentPage, pageSize, countryCodes, sortBy, isSortAsc]: [
          unknown,
          LinkingFilter,
          number,
          number,
          string[],
          string,
          boolean,
        ]) =>
          linkingService
            .getProducts(filters, currentPage, pageSize, countryCodes, sortBy, isSortAsc)
            .pipe(
              map((response: PaginationResponse<LinkingProduct[]>) =>
                LinkingActions.loadPurchaseProductsSuccess({ response })
              ),
              catchError(error =>
                of(
                  LinkingActions.loadPurchaseProductsFailure({
                    error: error?.message ?? 'Unknown error loading purchase products',
                  })
                )
              )
            )
      )
    );
  },
  { functional: true }
);

export const linkingEffects: object = {
  loadSalesDrugs$,
  loadPurchaseProducts$,
};
